import { useState, useEffect, useMemo } from 'react';
import { api, type MarketAnalysisHierarchyResponse, type StateAnalysis, type MarketAnalysis, type MarketDataZip, type ZumperMarketData, type ZillowRentalData, type ZillowSegment } from '../api/client';
import { Loading } from '../components/common';
import { RentHistogram } from '../components/RentHistogram';
import { HistoricalTrendsChart } from '../components/HistoricalTrendsChart';
import { formatCurrency } from '../utils/formatters';
import './MarketAnalysisPage.css';

// Helper to format bedroom data compactly
function formatBedroomSummary(
  data: {
    listings_1br?: number; listings_2br?: number; listings_3br?: number;
    listings_4br?: number; listings_5br?: number; listings_6br?: number;
    listings_7br?: number; listings_8br?: number;
  }
): string[] {
  const items: string[] = [];
  if (data.listings_1br) items.push(`1BR:${data.listings_1br}`);
  if (data.listings_2br) items.push(`2BR:${data.listings_2br}`);
  if (data.listings_3br) items.push(`3BR:${data.listings_3br}`);
  if (data.listings_4br) items.push(`4BR:${data.listings_4br}`);
  if (data.listings_5br) items.push(`5BR:${data.listings_5br}`);
  if (data.listings_6br) items.push(`6BR:${data.listings_6br}`);
  if (data.listings_7br) items.push(`7BR:${data.listings_7br}`);
  if (data.listings_8br) items.push(`8BR:${data.listings_8br}`);
  return items;
}

// Format bedroom cell with RentCast, Zumper, and Zillow rent comparison
function BedroomCell({ rent, props, zumperRent, zillowRent }: { rent?: number; props?: number; zumperRent?: number; zillowRent?: number }) {
  const hasRentCast = rent && rent > 0;
  const hasZumper = zumperRent && zumperRent > 0;
  const hasZillow = zillowRent && zillowRent > 0;
  const hasData = hasRentCast || hasZumper || hasZillow || (props && props > 0);

  if (!hasData) return <td className="br-cell">-</td>;

  return (
    <td className="br-cell">
      <div className="br-data-compare">
        {/* RentCast rent - primary (black) */}
        {hasRentCast ? (
          <span className="br-rent rc">{formatCurrency(rent)}</span>
        ) : (hasZumper || hasZillow) ? (
          <span className="br-rent rc no-data">-</span>
        ) : null}
        {/* Zumper rent - secondary (purple) */}
        {hasZumper && (
          <span className="br-rent zumper">{formatCurrency(zumperRent)}</span>
        )}
        {/* Zillow rent - tertiary (blue) */}
        {hasZillow && (
          <span className="br-rent zillow">{formatCurrency(zillowRent)}</span>
        )}
        {/* Property count */}
        {props && props > 0 && (
          <span className="br-props">({props})</span>
        )}
      </div>
    </td>
  );
}

// Format market temperature icon
function TemperatureBadge({ temp }: { temp?: string }) {
  if (!temp) return <span className="market-temp">-</span>;

  const tempLower = temp.toLowerCase();
  let icon = '🔵';
  if (tempLower.includes('hot')) icon = '🔥';
  else if (tempLower.includes('warm')) icon = '🟡';
  else if (tempLower.includes('cool') || tempLower.includes('cold')) icon = '❄️';

  return <span className="market-temp" title={temp}>{icon}</span>;
}

function getMarketDisplayName(slug: string): string {
  return slug
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function StateRow({
  state,
  markets,
  isExpanded,
  onToggle,
  expandedMarkets,
  onToggleMarket,
  marketZips,
  zumperData,
  zillowData,
  segmentData,
}: {
  state: StateAnalysis;
  markets: MarketAnalysis[];
  isExpanded: boolean;
  onToggle: () => void;
  expandedMarkets: Set<string>;
  onToggleMarket: (market: string) => void;
  marketZips: Record<string, MarketDataZip[]>;
  zumperData: Record<string, ZumperMarketData>;
  zillowData: Record<string, ZillowRentalData>;
  segmentData: Record<string, ZillowSegment[]>;
}) {
  const bedroomSummary = formatBedroomSummary(state);

  return (
    <div className="state-row">
      <div className="state-header" onClick={onToggle}>
        <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
        <span className="state-name">{state.state}</span>
        <div className="state-stats">
          <span className="stat">{state.market_count} market{state.market_count !== 1 ? 's' : ''}</span>
          <span className="stat">{state.property_count.toLocaleString()} properties</span>
          <span className="stat">{formatCurrency(state.avg_price)} avg</span>
          {state.avg_rent > 0 && (
            <span className="stat">{formatCurrency(state.avg_rent)}/mo</span>
          )}
          {state.avg_score > 0 && (
            <span className="stat score-stat">{state.avg_score} score</span>
          )}
        </div>
        {bedroomSummary.length > 0 && (
          <div className="bedroom-summary">
            {bedroomSummary.map((item, i) => (
              <span key={i} className="br-tag">{item}</span>
            ))}
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="markets-container">
          {markets.map(market => (
            <MarketRow
              key={market.market}
              market={market}
              isExpanded={expandedMarkets.has(market.market_slug || market.market)}
              onToggle={() => onToggleMarket(market.market_slug || market.market)}
              zips={marketZips[market.market_slug || market.market] || []}
              zumperData={zumperData[market.market_slug || '']}
              zillowData={zillowData[market.market_slug || '']}
              segments={segmentData[market.market_slug || market.market] || []}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MarketRow({
  market,
  isExpanded,
  onToggle,
  zips,
  zumperData,
  zillowData,
  segments,
}: {
  market: MarketAnalysis;
  isExpanded: boolean;
  onToggle: () => void;
  zips: MarketDataZip[];
  zumperData?: ZumperMarketData;
  zillowData?: ZillowRentalData;
  segments: ZillowSegment[];
}) {
  return (
    <div className="market-row">
      <div className="market-header" onClick={onToggle}>
        <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
        <span className="market-name">{getMarketDisplayName(market.market)}</span>
        <span className="stat">{market.zip_count} ZIP{market.zip_count !== 1 ? 's' : ''}</span>
        <span className="stat">{market.property_count.toLocaleString()} props</span>
        <span className="stat">{formatCurrency(market.avg_price)} avg</span>
      </div>

      {isExpanded && (
        <div className="zips-table">
          <table className="market-table">
            <thead>
              <tr>
                <th>Location</th>
                <th className="num-col">Props</th>
                <th className="num-col">Vacancy</th>
                <th className="num-col">TOM</th>
                <th className="num-col">Zillow DOM</th>
                <th className="num-col">Temp</th>
                <th className="demand-col">Demand</th>
                <th className="br-col">1BR</th>
                <th className="br-col">2BR</th>
                <th className="br-col">3BR</th>
                <th className="br-col">4BR</th>
                <th className="br-col">5BR</th>
                <th className="br-col">6BR+</th>
                <th className="num-col">Total</th>
                <th className="num-col">Growth</th>
              </tr>
            </thead>
            <tbody>
              {/* Market summary row - uses aggregated data from MarketAnalysis */}
              <tr className="market-summary-row">
                <td className="market-cell">{getMarketDisplayName(market.market)}</td>
                <td className="num-col">{market.property_count}</td>
                <td className="num-col">
                  {market.vacancy_rate > 0
                    ? `${(market.vacancy_rate * 100).toFixed(1)}%`
                    : zips[0]?.vacancy_rate !== undefined
                      ? `${(zips[0].vacancy_rate * 100).toFixed(1)}%`
                      : '-'}
                </td>
                <td className="num-col">
                  {market.avg_dom > 0
                    ? `${market.avg_dom}d`
                    : zips[0]?.metro_time_on_market !== undefined
                      ? `${zips[0].metro_time_on_market}d`
                      : '-'}
                </td>
                <td className="num-col">
                  {zillowData?.avg_days_on_market !== undefined ? `${zillowData.avg_days_on_market}d` : '-'}
                </td>
                <td className="num-col">
                  <TemperatureBadge temp={market.market_temp || zillowData?.market_temperature} />
                </td>
                <td className="demand-col">
                  {zips[0]?.demand_level ? (
                    <span className={`demand-badge demand-${zips[0].demand_level}`}>
                      {zips[0].demand_level.toUpperCase()}
                    </span>
                  ) : '-'}
                </td>
                <BedroomCell rent={market.avg_rent_1br} props={market.props_1br} zumperRent={zumperData?.median_rent_1br} zillowRent={zillowData?.rent_1br} />
                <BedroomCell rent={market.avg_rent_2br} props={market.props_2br} zumperRent={zumperData?.median_rent_2br} zillowRent={zillowData?.rent_2br} />
                <BedroomCell rent={market.avg_rent_3br} props={market.props_3br} zumperRent={zumperData?.median_rent_3br} zillowRent={zillowData?.rent_3br} />
                <BedroomCell rent={market.avg_rent_4br} props={market.props_4br} zumperRent={zumperData?.median_rent_4br} zillowRent={zillowData?.rent_4br} />
                <BedroomCell rent={market.avg_rent_5br} props={market.props_5br} />
                <BedroomCell
                  rent={market.avg_rent_6br || market.avg_rent_7br || market.avg_rent_8br}
                  props={market.props_6br_plus}
                />
                <td className="num-col">
                  {market.total_listings ?? '-'}
                  {zumperData?.listings_count ? <span className="zumper-count"> / {zumperData.listings_count}</span> : ''}
                  {zillowData?.available_rentals ? <span className="zillow-count"> / {zillowData.available_rentals}</span> : ''}
                </td>
                <td className={`num-col ${(market.avg_rent_growth ?? 0) >= 0 ? 'value-positive' : 'value-negative'}`}>
                  {market.avg_rent_growth !== 0 ? `${market.avg_rent_growth >= 0 ? '+' : ''}${market.avg_rent_growth.toFixed(1)}%` : '-'}
                  {zillowData?.yearly_change ? <span className={`zillow-growth ${zillowData.yearly_change >= 0 ? 'value-positive' : 'value-negative'}`}> / {zillowData.yearly_change >= 0 ? '+' : ''}{zillowData.yearly_change.toFixed(1)}%</span> : ''}
                </td>
              </tr>

              {/* ZIP rows */}
              {zips.map((zip) => (
                <tr key={zip.zip_code}>
                  <td className="zip-code">{zip.zip_code}{zip.city ? ` - ${zip.city}` : ''}</td>
                  <td className="num-col">{zip.property_count ?? '-'}</td>
                  <td className="num-col">
                    {zip.vacancy_rate !== undefined ? `${(zip.vacancy_rate * 100).toFixed(1)}%` : '-'}
                  </td>
                  <td className="num-col">
                    {zip.metro_time_on_market !== undefined ? `${zip.metro_time_on_market}d` : '-'}
                  </td>
                  <td className="num-col">-</td>
                  <td className="num-col">-</td>
                  <td className="demand-col">
                    {zip.demand_level ? (
                      <span className={`demand-badge demand-${zip.demand_level}`}>
                        {zip.demand_level.toUpperCase()}
                      </span>
                    ) : '-'}
                  </td>
                  <BedroomCell rent={zip.rent_1br} props={zip.props_1br} />
                  <BedroomCell rent={zip.rent_2br} props={zip.props_2br} />
                  <BedroomCell rent={zip.rent_3br} props={zip.props_3br} />
                  <BedroomCell rent={zip.rent_4br} props={zip.props_4br} />
                  <BedroomCell rent={zip.rent_5br} props={zip.props_5br} />
                  <BedroomCell
                    rent={zip.rent_6br || zip.rent_7br || zip.rent_8br}
                    props={zip.props_6br_plus}
                  />
                  <td className="num-col">{zip.total_listings ?? '-'}</td>
                  <td className={`num-col ${Number(zip.rent_growth_pct ?? 0) >= 0 ? 'value-positive' : 'value-negative'}`}>
                    {zip.rent_growth_pct !== undefined ? `${Number(zip.rent_growth_pct) >= 0 ? '+' : ''}${Number(zip.rent_growth_pct).toFixed(1)}%` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Rent Histogram from Zillow Segment Data */}
          {segments.length > 0 && segments[0]?.rent_histogram && segments[0].rent_histogram.length > 0 && (
            <div className="market-histogram-section">
              <RentHistogram
                data={segments[0].rent_histogram}
                title={`Rent Distribution - ${getMarketDisplayName(market.market)}`}
                highlightPrice={zillowData?.median_rent}
              />
              {segments[0].bedroom_rents && Object.keys(segments[0].bedroom_rents).length > 0 && (
                <div className="segment-bedroom-rents">
                  <span className="label">Zillow Bedroom Rents:</span>
                  {Object.entries(segments[0].bedroom_rents).map(([beds, rent]) => (
                    <span key={beds} className="bedroom-rent-tag">
                      {beds}: {formatCurrency(rent)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MarketAnalysisPage() {
  const [hierarchy, setHierarchy] = useState<MarketAnalysisHierarchyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedStates, setExpandedStates] = useState<Set<string>>(new Set());
  const [expandedMarkets, setExpandedMarkets] = useState<Set<string>>(new Set());
  const [marketZips, setMarketZips] = useState<Record<string, MarketDataZip[]>>({});
  const [zumperData, setZumperData] = useState<Record<string, ZumperMarketData>>({});
  const [zillowData, setZillowData] = useState<Record<string, ZillowRentalData>>({});
  const [segmentData, setSegmentData] = useState<Record<string, ZillowSegment[]>>({});
  const [selectedTrendsMarket, setSelectedTrendsMarket] = useState<string>('');

  const [rentcastToken, setRentcastToken] = useState<string>(() => {
    return localStorage.getItem('rentcastToken') || '';
  });

  // Get list of all markets for the dropdown
  const allMarkets = useMemo(() => {
    if (!hierarchy) return [];
    return hierarchy.markets
      .map(m => ({ slug: m.market, name: getMarketDisplayName(m.market), state: m.state }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [hierarchy]);

  // Save token to localStorage when it changes
  const handleTokenChange = (token: string) => {
    setRentcastToken(token);
    localStorage.setItem('rentcastToken', token);
  };

  // Load hierarchy, Zumper, and Zillow data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch hierarchy, Zumper, and Zillow data in parallel
        const [hierarchyData, zumperResponse, zillowResponse] = await Promise.all([
          api.getMarketAnalysisHierarchy(),
          api.getZumperMarkets().catch(() => ({ markets: [] })),
          api.getZillowMarkets().catch(() => ({ markets: [] })),
        ]);

        setHierarchy(hierarchyData);

        // Build a map of market_slug -> ZumperMarketData
        const zumperMap: Record<string, ZumperMarketData> = {};
        for (const z of zumperResponse.markets) {
          if (z.market_slug) {
            zumperMap[z.market_slug] = z;
          }
        }
        setZumperData(zumperMap);

        // Build a map of market_slug -> ZillowRentalData
        const zillowMap: Record<string, ZillowRentalData> = {};
        for (const z of zillowResponse.markets) {
          if (z.market_slug) {
            zillowMap[z.market_slug] = z;
          }
        }
        setZillowData(zillowMap);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load market analysis data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const toggleState = (state: string) => {
    const newExpanded = new Set(expandedStates);
    if (newExpanded.has(state)) {
      newExpanded.delete(state);
    } else {
      newExpanded.add(state);
    }
    setExpandedStates(newExpanded);
  };

  const toggleMarket = async (market: string) => {
    const newExpanded = new Set(expandedMarkets);

    if (newExpanded.has(market)) {
      newExpanded.delete(market);
      setExpandedMarkets(newExpanded);
      return;
    }

    // Expand and fetch ZIPs if not cached
    newExpanded.add(market);
    setExpandedMarkets(newExpanded);

    // Fetch ZIPs and segments in parallel if not cached
    const fetchPromises: Promise<void>[] = [];

    if (!marketZips[market]) {
      fetchPromises.push(
        api.getMarketDataByMarket(market)
          .then(data => setMarketZips(prev => ({ ...prev, [market]: data.zips })))
          .catch(err => {
            console.error(`Failed to load ZIPs for ${market}:`, err);
            setMarketZips(prev => ({ ...prev, [market]: [] }));
          })
      );
    }

    if (!segmentData[market]) {
      fetchPromises.push(
        api.getZillowSegments(market)
          .then(data => setSegmentData(prev => ({ ...prev, [market]: data.segments })))
          .catch(err => {
            console.error(`Failed to load segments for ${market}:`, err);
            setSegmentData(prev => ({ ...prev, [market]: [] }));
          })
      );
    }

    if (fetchPromises.length > 0) {
      await Promise.all(fetchPromises);
    }
  };

  // Calculate totals
  const totalProperties = hierarchy?.states.reduce((sum, s) => sum + s.property_count, 0) ?? 0;
  const totalMarkets = hierarchy?.states.reduce((sum, s) => sum + s.market_count, 0) ?? 0;

  return (
    <div className="market-analysis-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1>Market Analysis</h1>
            <p>Rental market data by State → Market → ZIP with bedroom breakdown</p>
          </div>
          <div className="header-actions">
            <div className="token-input-group">
              <label htmlFor="rentcast-token">RentCast Token:</label>
              <input
                id="rentcast-token"
                type="password"
                placeholder="Enter API token"
                value={rentcastToken}
                onChange={(e) => handleTokenChange(e.target.value)}
                className="token-input"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        {loading ? (
          <Loading message="Loading market analysis..." />
        ) : hierarchy ? (
          <>
            {/* Summary Cards */}
            <div className="market-summary-cards">
              <div className="summary-card">
                <span className="summary-value">{hierarchy.states.length}</span>
                <span className="summary-label">States</span>
              </div>
              <div className="summary-card">
                <span className="summary-value">{totalMarkets}</span>
                <span className="summary-label">Markets</span>
              </div>
              <div className="summary-card">
                <span className="summary-value">{totalProperties.toLocaleString()}</span>
                <span className="summary-label">Properties</span>
              </div>
            </div>

            {/* Historical Trends Section */}
            <div className="trends-section">
              <div className="trends-header">
                <h2>Historical Trends</h2>
                <select
                  className="market-select"
                  value={selectedTrendsMarket}
                  onChange={(e) => setSelectedTrendsMarket(e.target.value)}
                >
                  <option value="">Select a market...</option>
                  {allMarkets.map(m => (
                    <option key={m.slug} value={m.slug}>
                      {m.name} ({m.state})
                    </option>
                  ))}
                </select>
              </div>
              {selectedTrendsMarket && (
                <HistoricalTrendsChart
                  market={selectedTrendsMarket}
                  title={`Historical Trends - ${getMarketDisplayName(selectedTrendsMarket)}`}
                />
              )}
              {!selectedTrendsMarket && (
                <div className="trends-placeholder">
                  Select a market above to view historical rent and housing trends
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="table-legend">
              <span className="legend-item">Rent data: <span className="rc-label">RentCast</span> / <span className="zumper-label">Zumper</span> / <span className="zillow-label">Zillow</span></span>
              <span className="legend-item">Each cell shows: <strong>$Rent (# props)</strong></span>
              <span className="legend-item">Temp: <span className="market-temp">🔥</span>=Hot <span className="market-temp">🟡</span>=Warm <span className="market-temp">❄️</span>=Cool</span>
              <span className="legend-item">Demand: <span className="demand-badge demand-high">HIGH</span> = low vacancy + fast absorption, <span className="demand-badge demand-weak">WEAK</span> = high vacancy</span>
            </div>

            {/* Hierarchy View */}
            <div className="market-analysis-hierarchy">
              {hierarchy.states.map(state => {
                const stateMarkets = hierarchy.markets.filter(m => m.state === state.state);
                return (
                  <StateRow
                    key={state.state}
                    state={state}
                    markets={stateMarkets}
                    isExpanded={expandedStates.has(state.state)}
                    onToggle={() => toggleState(state.state)}
                    expandedMarkets={expandedMarkets}
                    onToggleMarket={toggleMarket}
                    marketZips={marketZips}
                    zumperData={zumperData}
                    zillowData={zillowData}
                    segmentData={segmentData}
                  />
                );
              })}
            </div>

            {hierarchy.states.length === 0 && (
              <div className="no-data">
                <h3>No Data Available</h3>
                <p>Run a crawl to populate property data, then refresh market data from RentCast.</p>
              </div>
            )}
          </>
        ) : (
          <div className="no-data">
            <h3>No Data Available</h3>
            <p>Failed to load market analysis data.</p>
          </div>
        )}
      </div>
    </div>
  );
}
